/**
 * Documents contracts (docs/02 §Documents; ADR-43).
 *
 * SharePoint is the file authority; PropOS stores meaning and relationships.
 * The vocabulary matters and is enforced here: a **document link** is the
 * SharePoint (or external) URL — the internal relation is an **association**.
 *
 * `relationshipType` is ORTHOGONAL to the document's type: the type says what
 * the document IS ("Lease"), the relationship says what role it plays HERE
 * (primary, amendment, exhibit …).
 */

import { z } from 'zod';

import { partialUpdateSchema } from './partial-update.ts';

/** Every record a document can be associated to (0031 CHECK, verbatim). */
export const ASSOCIATION_ENTITY_TYPES = [
  'property',
  'space',
  'property_group',
  'tenant_org',
  'tenancy',
  'lease',
  'vendor',
  'vendor_contact',
  'agreement',
  'utility_account',
  'management_agreement',
] as const;
export type AssociationEntityType = (typeof ASSOCIATION_ENTITY_TYPES)[number];

export const RELATIONSHIP_TYPES = [
  'primary',
  'amendment',
  'exhibit',
  'supporting',
  'correspondence',
  'other',
] as const;
export type RelationshipType = (typeof RELATIONSHIP_TYPES)[number];

/** Role in prose, beside the enum so two screens cannot word it differently. */
export const RELATIONSHIP_LABEL: Record<RelationshipType, string> = {
  primary: 'Primary document',
  amendment: 'Amendment',
  exhibit: 'Exhibit',
  supporting: 'Supporting',
  correspondence: 'Correspondence',
  other: 'Other',
};

export const associationEntityTypeSchema = z.enum(ASSOCIATION_ENTITY_TYPES);
export const relationshipTypeSchema = z.enum(RELATIONSHIP_TYPES);

/** GET /api/v1/documents/explore — one record's Explorer (docs/03 §2c). */
export const explorerQuerySchema = z.object({
  entityType: associationEntityTypeSchema,
  entityId: z.uuid(),
  /** The ONE path to history: superseded and un-linked associations included. */
  includeInactive: z.stringbool().default(false),
});

/**
 * One reason a document appears on this record. A document reachable by several
 * paths carries several of these — it is never several rows.
 */
export interface ExplorerVia {
  readonly associationId: string;
  /** 'direct' when filed on this record itself; otherwise the source kind. */
  readonly kind: string;
  /** Reader-facing: "This property", "Suite 210", "Agreement: Portfolio HVAC". */
  readonly label: string;
  readonly entityType: AssociationEntityType;
  readonly entityId: string;
  readonly relationshipType: RelationshipType;
  readonly status: 'active' | 'superseded' | 'inactive';
  /** Superseded: what replaced it, when, and by whom (docs/03 §2c). */
  readonly supersededByTitle: string | null;
  readonly supersededAt: string | null;
  readonly supersededByName: string | null;
  /** Un-linked: the removal story (0031). */
  readonly inactivatedReason: string | null;
  readonly inactivatedAt: string | null;
  readonly inactivatedByName: string | null;
}

export interface ExplorerRow {
  readonly documentId: string;
  readonly title: string;
  readonly url: string;
  readonly source: 'sharepoint_file' | 'sharepoint_folder' | 'external_url';
  readonly typeName: string | null;
  readonly verifyStatus: 'valid' | 'unverified' | 'moved' | 'broken';
  /** DOCUMENT-INTRINSIC validity (a COI's expiry), never a business term. */
  readonly validFrom: string | null;
  readonly validUntil: string | null;
  /**
   * Whether THIS caller may change THIS document (punch 6.1a correction).
   *
   * Straight from `app_can_edit_document(d.id)` in the Explorer query — the same
   * function the UPDATE policy and every write endpoint use. It is on the row
   * because a record's Documents tab has to decide, per row, whether Replace and
   * Un-link are offered, and the rule (access mode OR ownership OR elevated
   * manage, all ANDed with `documents.edit`) is not something React can
   * reconstruct: the browser knows the caller's permissions but not the
   * document's `created_by`. Reconstructing it would be a second copy of the
   * policy, and the copy would be wrong.
   *
   * It is the DOCUMENT half only. Associating still also needs `documents.link`
   * and property scope over the record (0036) — the tab ANDs the two.
   */
  readonly canEdit: boolean;
  readonly vias: readonly ExplorerVia[];
}

/**
 * `GET /api/v1/documents/explore` — the rows PLUS the target record's own
 * association authority (punch 6.1a, second correction).
 *
 * The decision has two halves and both come from the database. `row.canEdit` is
 * the DOCUMENT half (`app_can_edit_document`); `canAssociate` is the
 * TARGET-RECORD half — `app_can_edit_any_property('documents.link',
 * app_association_properties(...))`, the exact predicate
 * `document_associations_write` enforces with.
 *
 * It is on the envelope rather than on each row because it is a property of the
 * record being explored, not of any one document. And it is not the
 * `documents.link` permission key: reads are universal, so a scoped manager can
 * open a record they cannot write to, and the key alone would offer them Link,
 * Replace and Un-link on it.
 */
export interface ExplorerResponse {
  readonly rows: readonly ExplorerRow[];
  /**
   * The target-record half of association authority. It alone governs UNLINK,
   * which stays available beneath a retired vendor (the removal exception).
   */
  readonly canAssociate: boolean;
  /**
   * Whether a NEW or REPLACEMENT link may be made here (CR-027): `canAssociate`
   * AND the owning vendor — for a vendor, vendor contact, agreement or utility
   * account — is active. Link and Replace read this; Unlink does not.
   */
  readonly canLink: boolean;
  /** Why Link and Replace are unavailable here (a retired owning vendor), or null. */
  readonly linkBlockedReason: string | null;
}

/** POST /api/v1/documents/links/:id/supersede — replace, keeping the history. */
export const supersedeAssociationSchema = z.object({
  /** The document that takes over this role on this record. */
  documentId: z.uuid(),
});

/** POST /api/v1/documents/links/:id/inactivate — un-link, with the story. */
export const inactivateAssociationSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

// ---------------------------------------------------------------------------
// The document itself (the /documents browser and its detail)
// ---------------------------------------------------------------------------

export const DOCUMENT_SOURCES = ['sharepoint_file', 'sharepoint_folder', 'external_url'] as const;
export type DocumentSource = (typeof DOCUMENT_SOURCES)[number];

/**
 * Where a document lives, in words. Beside the enum rather than on the screen
 * that renders it: `sharepoint_file` is the kind of value this repo has already
 * shipped to a reader once (docs/11 X-2), and the label belongs with the list it
 * labels, the way `RELATIONSHIP_LABEL` below does.
 */
export const DOCUMENT_SOURCE_LABEL: Record<DocumentSource, string> = {
  sharepoint_file: 'SharePoint file',
  sharepoint_folder: 'SharePoint folder',
  external_url: 'External URL',
};

export const VERIFY_STATUSES = ['valid', 'unverified', 'moved', 'broken'] as const;
export type VerifyStatus = (typeof VERIFY_STATUSES)[number];

/**
 * A folder link is a real thing, not a lesser file: a property's "Photos" or a
 * vendor's "Invoices" is one SharePoint folder that grows, and pointing at the
 * folder is more honest than pointing at whichever file was there in March.
 * GENERATED from `source` in the database, so the two cannot disagree.
 */
export type ResourceType = 'file' | 'folder';

/**
 * Who may CHANGE a document (punch 6.1a; Matt's decision 2026-09-02).
 *
 * docs/12 §3a settles reading — every approved user reads everything — and both
 * modes leave that alone. These govern the WRITE side, which for a document
 * could not be answered by property scope: an unattached document has no
 * property, so `documents_update` fell back to one unscoped key held by two of
 * the six roles. The document now carries the answer itself.
 *
 * Neither mode touches SharePoint or SharePoint permissions. They govern the
 * PropOS document record and its associations, which is all this app owns.
 */
export const DOCUMENT_ACCESS_MODES = ['organization_read', 'organization_edit'] as const;
export type DocumentAccessMode = (typeof DOCUMENT_ACCESS_MODES)[number];

/**
 * The words on the control. "Everyone" means the holders of `documents.edit` —
 * four of the six base roles — and NOT literally every approved user:
 * `read_only` holds `documents.view` alone by design, and a maintenance
 * technician holds no document write key at all. The hints below are what keep
 * the label from over-promising, and docs/12's matrix names the four.
 */
export const DOCUMENT_ACCESS_MODE_LABEL: Record<DocumentAccessMode, string> = {
  organization_read: 'Everyone can view',
  organization_edit: 'Everyone can edit',
};

export const DOCUMENT_ACCESS_MODE_HINT: Record<DocumentAccessMode, string> = {
  organization_read:
    'Anyone with document access can open it. Only its owner, a service coordinator or an administrator can change its details or what it is filed against.',
  organization_edit:
    'Anyone who can edit documents can also change this one’s details and what it is filed against. Changing this setting still takes its owner or a manager.',
};

export const documentWriteSchema = z.object({
  source: z.enum(DOCUMENT_SOURCES),
  url: z.url().max(4000),
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().max(4000).nullish(),
  docTypeId: z.uuid().nullish(),
  /**
   * DOCUMENT-INTRINSIC validity (ADR-43 one authority per fact): a COI or a
   * permit expires on its own terms. An agreement's or lease's TERM belongs to
   * that business record and is never restated here.
   */
  validFrom: z.iso.date().nullish(),
  validUntil: z.iso.date().nullish(),
  /**
   * Optional on the wire and defaulted here, so a caller that predates 6.1a
   * creates the conservative mode rather than failing validation — the same
   * value the column defaults to, in the one place a reader would look for it.
   *
   * CHANGING it is a manage-level operation, enforced by the service and by a
   * trigger in 0083, not by this schema: a partial update cannot express "this
   * field needs a different permission from its neighbours".
   */
  accessMode: z.enum(DOCUMENT_ACCESS_MODES).default('organization_read'),
});
export type DocumentWrite = z.infer<typeof documentWriteSchema>;

export const documentUpdateSchema = z.object({
  version: z.number().int().positive(),
  changes: partialUpdateSchema(documentWriteSchema.omit({ source: true })),
});

/**
 * How a document's own validity reads today — the same vocabulary, and the same
 * window (`EXPIRING_SOON_DAYS`, O-27), as the COI chip. `dated` is the honest
 * name for "has a validity at all": most documents have none, so "no dates" is
 * not a defect to be filtered out, it is the norm.
 */
export const VALIDITY_FILTERS = ['any', 'expiring', 'expired', 'dated'] as const;
export type ValidityFilter = (typeof VALIDITY_FILTERS)[number];

/** Whether a document is attached to anything — "what is filed nowhere?" */
export const LINKED_FILTERS = ['any', 'linked', 'unlinked'] as const;
export type LinkedFilter = (typeof LINKED_FILTERS)[number];

export const documentListQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  verifyStatus: z.enum(VERIFY_STATUSES).optional(),
  docTypeId: z.uuid().optional(),
  /** file | folder — the spec's §17 facet, and a real distinction. */
  resourceType: z.enum(['file', 'folder']).optional(),
  linked: z.enum(LINKED_FILTERS).default('any'),
  validity: z.enum(VALIDITY_FILTERS).default('any'),
  /**
   * "Only documents I may change" (punch 6.1a correction). Not a facet on the
   * browser — it exists for the PICKERS on a record's Documents tab, which must
   * not offer a document whose association the server would then refuse.
   *
   * It has to be a SERVER filter rather than a `.filter()` in React: the read is
   * capped at 200 rows, so narrowing after the cap would hide eligible
   * documents behind ineligible ones the caller can never use.
   */
  editable: z.stringbool().optional(),
});
export type DocumentListQuery = z.infer<typeof documentListQuerySchema>;

export interface DocumentRow {
  readonly id: string;
  readonly source: DocumentSource;
  readonly resourceType: ResourceType;
  readonly isExternal: boolean;
  readonly url: string;
  readonly title: string;
  readonly typeName: string | null;
  readonly verifyStatus: VerifyStatus;
  readonly lastVerifiedAt: string | null;
  readonly validFrom: string | null;
  readonly validUntil: string | null;
  /** ACTIVE associations only — a document unlinked everywhere reads as 0. */
  readonly linkCount: number;
  readonly updatedAt: string;
  readonly version: number;
  /** What the document says about who may change it (punch 6.1a). */
  readonly accessMode: DocumentAccessMode;
  /**
   * Whether THIS caller may change it, and whether they may change its access
   * policy — answered by the database's own `app_can_edit_document` /
   * `app_can_manage_document`, never recomputed here.
   *
   * The row carries the answers so a screen can disable a control it must not
   * offer (docs/04 §7: a disabled control states its reason). A client that
   * derived them from `accessMode` and its own permissions would be the second
   * permission model this design exists to avoid — and it would be wrong the
   * day a named-user grant lands, because the rule would have moved and the
   * copy would not have.
   */
  readonly canEdit: boolean;
  readonly canManage: boolean;
}
